var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import GET_MODEL_SPECIFICATION_AVERAGE_VARIANT_COST_DETAILS from 'api/queries/GET_MODEL_SPECIFICATION_AVERAGE_VARIANT_COST_DETAILS';
import GET_MODEL_SPECIFICATION_VARIANT_COST_DETAILS from 'api/queries/GET_MODEL_SPECIFICATION_VARIANT_COST_DETAILS';
import GET_SPECIFICATION_COSTS from 'api/queries/GET_SPECIFICATION_COSTS';
import client from '../api/environment';
const initialState = {
    sizes: [],
    colors: [],
    variants: [],
    variantCostDetails: {},
    averageCostDetails: {},
    status: {
        fetch: 'idle',
        fetchCostDetails: 'idle',
        fetchCostAverage: 'idle',
    },
    errors: {
        fetch: null,
        fetchCostDetails: null,
        fetchCostAverage: null,
    },
    refetchFlags: {
        fetch: false,
        fetchCostDetails: false,
        fetchCostAverage: false,
    },
};
export const fetchSpecificationCosts = createAsyncThunk('specificationCosts/fetchSpecificationCosts', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ specificationId }, { rejectWithValue }) {
    try {
        const specificationCostsData = yield client.query({
            query: GET_SPECIFICATION_COSTS,
            variables: { modelSpecificationId: specificationId },
            fetchPolicy: 'network-only',
        });
        return {
            sizes: specificationCostsData.data.node.sizes.edges.map((size) => size.node),
            colors: specificationCostsData.data.node.colors.edges.map((color) => color.node),
            variants: specificationCostsData.data.node.variants.edges.map((variant) => variant.node),
        };
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchSpecificationVariantCostDetails = createAsyncThunk('specificationComponentsCosts/fetchSpecificationVariantCostDetails', (modelSpecificationVariantId_1, _a) => __awaiter(void 0, [modelSpecificationVariantId_1, _a], void 0, function* (modelSpecificationVariantId, { rejectWithValue }) {
    try {
        const specificationVariantCostDetails = yield client.query({
            query: GET_MODEL_SPECIFICATION_VARIANT_COST_DETAILS,
            variables: { modelSpecificationVariantId },
            fetchPolicy: 'network-only',
        });
        return specificationVariantCostDetails.data.node;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchSpecificationAverageCostDetails = createAsyncThunk('specificationComponentsCosts/fetchSpecificationAverageCostDetails', (modelSpecificationId_1, _a) => __awaiter(void 0, [modelSpecificationId_1, _a], void 0, function* (modelSpecificationId, { rejectWithValue }) {
    try {
        const specificationVariantCostDetails = yield client.query({
            query: GET_MODEL_SPECIFICATION_AVERAGE_VARIANT_COST_DETAILS,
            variables: { modelSpecificationId },
            fetchPolicy: 'network-only',
        });
        return specificationVariantCostDetails.data.node;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
const specificationCostsSlice = createSlice({
    name: 'specificationCosts',
    initialState,
    reducers: {
        resetSpecificationCosts(state) {
            state.sizes = [];
            state.colors = [];
            state.variants = [];
            state.variantCostDetails = {};
            state.averageCostDetails = {};
            state.status = initialState.status;
            state.errors = initialState.errors;
            state.refetchFlags = initialState.refetchFlags;
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchSpecificationCosts
            .addCase(fetchSpecificationCosts.pending, (state) => {
            state.status.fetch = 'loading';
        })
            .addCase(fetchSpecificationCosts.fulfilled, (state, action) => {
            state.status.fetch = 'succeeded';
            state.sizes = action.payload.sizes;
            state.colors = action.payload.colors;
            state.variants = action.payload.variants;
        })
            .addCase(fetchSpecificationCosts.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch = action.payload || 'Failed to fetch specification costs';
        })
            // fetchSpecificationVariantCostDetails
            .addCase(fetchSpecificationVariantCostDetails.pending, (state) => {
            state.status.fetchCostDetails = 'loading';
        })
            .addCase(fetchSpecificationVariantCostDetails.fulfilled, (state, action) => {
            state.status.fetchCostDetails = 'succeeded';
            state.variantCostDetails = action.payload;
        })
            .addCase(fetchSpecificationVariantCostDetails.rejected, (state, action) => {
            state.status.fetchCostDetails = 'failed';
            state.errors.fetchCostDetails =
                action.payload || 'Failed to fetch specification production quantities';
        })
            // fetchSpecificationAverageCostDetails
            .addCase(fetchSpecificationAverageCostDetails.pending, (state) => {
            state.status.fetchCostAverage = 'loading';
        })
            .addCase(fetchSpecificationAverageCostDetails.fulfilled, (state, action) => {
            state.status.fetchCostAverage = 'succeeded';
            state.averageCostDetails = action.payload;
        })
            .addCase(fetchSpecificationAverageCostDetails.rejected, (state, action) => {
            state.status.fetchCostAverage = 'failed';
            state.errors.fetchCostAverage =
                action.payload || 'Failed to fetch specification production quantities';
        });
    },
});
export const { resetSpecificationCosts } = specificationCostsSlice.actions;
export const selectSpecificationCostsStatus = (state) => state.specificationCosts.status;
export const selectSpecificationCostsErrors = (state) => state.specificationCosts.errros;
export const selectSpecificationCostsRefetchFlags = (state) => state.specificationCosts.refetchFlags;
export const selectSpecificationCostsSizes = (state) => state.specificationCosts.sizes;
export const selectSpecificationCostsColors = (state) => state.specificationCosts.colors;
export const selectSpecificationCostsVariants = (state) => state.specificationCosts.variants;
export const selectSpecificationCostsVariantDetails = (state) => state.specificationCosts.variantCostDetails;
export const selectSpecificationCostsAverageDetails = (state) => state.specificationCosts.averageCostDetails;
export default specificationCostsSlice.reducer;
